import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { apiErrorDetail } from '@/lib/apiError'
import { parseDateOnly } from '@/lib/formatters'
import { type ParsedDates, useSetParsedDates } from '@/models/document'

const FIELDS: { key: keyof ParsedDates; label: string }[] = [
  { key: 'dateEffective', label: 'Effective' },
  { key: 'dateExpiration', label: 'Expires' },
  { key: 'dateIssued', label: 'Issued' },
]

function toDate(value: unknown): Date | undefined {
  return typeof value === 'string' && value ? (parseDateOnly(value) ?? undefined) : undefined
}

/** The three dates the vault reads, corrected by hand when the parser got one wrong. */
export function DatesEditor({
  orgId,
  documentId,
  attachmentId,
  data,
  onDone,
}: {
  orgId: string
  documentId: string
  /** The file the dates belong to; may be omitted when the document has just one. */
  attachmentId?: string | null
  data: Record<string, unknown>
  onDone: () => void
}) {
  const [dates, setDates] = useState<Record<keyof ParsedDates, Date | undefined>>({
    dateEffective: toDate(data.dateEffective),
    dateExpiration: toDate(data.dateExpiration),
    dateIssued: toDate(data.dateIssued),
  })
  const save = useSetParsedDates(orgId, documentId)

  const submit = () => {
    save.mutate(
      {
        attachmentId: attachmentId ?? undefined,
        dateEffective: dates.dateEffective ? format(dates.dateEffective, 'yyyy-MM-dd') : null,
        dateExpiration: dates.dateExpiration ? format(dates.dateExpiration, 'yyyy-MM-dd') : null,
        dateIssued: dates.dateIssued ? format(dates.dateIssued, 'yyyy-MM-dd') : null,
      },
      {
        onSuccess: () => {
          toast.success('Dates saved.')
          onDone()
        },
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not save the dates.')),
      }
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {FIELDS.map(({ key, label }) => (
        <div key={key} className="grid grid-cols-[9rem_1fr] items-center gap-3">
          <span className="text-muted-foreground text-sm">{label}</span>
          <DateInput
            inputProps={{ 'aria-label': label }}
            value={dates[key]}
            onChange={(date) => setDates((prev) => ({ ...prev, [key]: date }))}
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" loading={save.isPending ? 'Saving…' : undefined} onClick={submit}>
          Save dates
        </Button>
        <Button size="sm" variant="outline" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
